import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface NotificationPreferences {
  sms: boolean;
  contact_form: boolean;
  phone_call: boolean;
}

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, [user]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      
      if (subscription && user) {
        // Verify subscription in database
        const { data } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint)
          .eq('is_active', true)
          .maybeSingle();
        
        setIsSubscribed(!!data);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribe = async (preferences?: NotificationPreferences) => {
    if (!isSupported || !user) {
      toast.error('Push notifications are not supported');
      return;
    }

    setLoading(true);

    try {
      console.log('🔔 Starting push notification subscription...');
      
      // Check if service worker is registered
      if (!navigator.serviceWorker.controller) {
        console.error('❌ No service worker controller');
        toast.error('Service worker not ready. Please refresh the page.');
        setLoading(false);
        return;
      }

      console.log('✅ Service worker is active');

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      console.log('📱 Notification permission:', perm);

      if (perm !== 'granted') {
        toast.error('Notification permission denied');
        setLoading(false);
        return;
      }

      // Get VAPID public key from server
      console.log('🔑 Fetching VAPID public key...');
      const { data: vapidData, error: vapidError } = await supabase.functions.invoke('get-vapid-keys');
      
      if (vapidError || !vapidData?.publicKey) {
        console.error('❌ Failed to get VAPID key:', vapidError);
        toast.error('Failed to configure push notifications');
        setLoading(false);
        return;
      }

      console.log('✅ Got VAPID public key:', vapidData.publicKey.substring(0, 20) + '...');

      // Get service worker registration
      console.log('📝 Getting service worker registration...');
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ Service worker ready');

      // Convert VAPID key
      console.log('🔄 Converting VAPID key to Uint8Array...');
      const applicationServerKey = urlBase64ToUint8Array(vapidData.publicKey);
      console.log('✅ Converted key length:', applicationServerKey.length, 'bytes');

      // Subscribe to push notifications
      console.log('📲 Subscribing to push manager...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource
      });

      console.log('✅ Push subscription created:', {
        endpoint: subscription.endpoint.substring(0, 50) + '...',
        hasP256dh: !!subscription.getKey('p256dh'),
        hasAuth: !!subscription.getKey('auth')
      });

      // Save subscription to database
      console.log('💾 Saving subscription to database...');
      const subscriptionData = {
        user_id: user.id,
        endpoint: subscription.endpoint,
        p256dh_key: arrayBufferToBase64(subscription.getKey('p256dh')),
        auth_key: arrayBufferToBase64(subscription.getKey('auth')),
        notification_types: preferences || {
          sms: true,
          contact_form: true,
          phone_call: true
        } as any,
        is_active: true
      };

      const { error } = await supabase
        .from('push_subscriptions')
        .upsert([subscriptionData], {
          onConflict: 'endpoint'
        });

      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }

      console.log('✅ Subscription saved to database');
      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error: any) {
      console.error('❌ Error subscribing to push notifications:', error);
      console.error('Error details:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack
      });
      
      let errorMessage = 'Failed to enable push notifications';
      if (error?.message?.includes('not supported')) {
        errorMessage = 'Push notifications not supported in this browser';
      } else if (error?.message?.includes('denied')) {
        errorMessage = 'Notification permission was denied';
      } else if (error?.message?.includes('InvalidStateError')) {
        errorMessage = 'Service worker is not ready. Please refresh the page.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    if (!user) return;

    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Remove from database
        await supabase
          .from('push_subscriptions')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint);
      }

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (preferences: NotificationPreferences) => {
    if (!user) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        toast.error('No active subscription found');
        return;
      }

      const { error } = await supabase
        .from('push_subscriptions')
        .update({ notification_types: preferences as any })
        .eq('user_id', user.id)
        .eq('endpoint', subscription.endpoint);

      if (error) throw error;

      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    updatePreferences
  };
};

// Helper functions
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
